import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';

const Box = styled.div<{ $posted?: boolean }>`
  border: solid 1px ${({ theme }) => theme.colors.grayscale};
  padding: 12px 16px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  min-height: 120px;
`;

const Actions = styled.div<{ $isPosted: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  ${({ $isPosted, theme }) =>
    $isPosted &&
    css`
      button:last-of-type {
        color: ${theme.colors.success};
        border-color: ${theme.colors.success};
      }
    `}
`;

const Input = styled(Text)`
  border: 0;
  outline: none;
  resize: none;
`;

interface Props {
  message?: string;
  version: 1 | 2;
  isPosted: boolean;
  commentId: number;
}

const Comment: FC<Props> = ({ message = '', version, isPosted, commentId }) => {
  const [value, setValue] = useState(message);

  const queryClient = useQueryClient();

  const alerts = useAlerts();
  const { id } = useParams();

  const onPost = async () => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) {
        return;
      }

      await api.post(
        `/v1/editorial/facebook-post-comments/${commentId}/reply`,
        {
          version,
          message: value,
        },
      );

      await queryClient.invalidateQueries([
        'editorial_distribution_schedules',
        {
          id,
        },
      ]);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  useEffect(() => {
    setValue(message);
  }, [message]);

  return (
    <Box>
      <Input
        $type="smallBold"
        as={TextareaAutosize}
        value={value}
        placeholder="Enter reply"
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
          setValue(e.target.value)
        }
      />
      <Actions $isPosted={isPosted}>
        <Button $small onClick={onPost}>
          Post{isPosted && 'ed'}
        </Button>
      </Actions>
    </Box>
  );
};

export default Comment;
